import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Users, Search, Clock, RefreshCw,
  X, ExternalLink, MoreHorizontal,
  FileWarning, ShieldAlert, CheckCircle,
  ChevronLeft, ChevronRight, ChevronDown
} from 'lucide-react';
import api from '../../services/apiService';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, bg, border }) => (
  <div className={`${bg} ${border || 'border-slate-200'} border p-4 rounded-2xl shadow-sm relative overflow-hidden group hover:border-orange-500 transition-all flex items-center gap-3`}>
    <div className={`p-2.5 rounded-xl ${color} bg-white/50 backdrop-blur-sm border border-slate-100 shadow-sm`}>
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</div>
      <div className={`text-xl font-black ${color} tracking-tighter`}>{value}</div>
    </div>
  </div>
);

// Filter Tab Component
const FilterTab = ({ active, onClick, label, count, color }) => (
  <button
    onClick={onClick}
    className={`px-8 py-5 text-[10px] font-black uppercase tracking-widest transition-all relative flex items-center gap-2 ${
      active ? 'text-slate-900' : 'text-slate-400 hover:text-slate-600'
    }`}
  >
    {label}
    <span className={`px-2 py-0.5 rounded-lg text-[9px] ${
      active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'
    }`}>{count}</span>
    {active && <div className="absolute bottom-0 left-0 right-0 h-1 bg-orange-500 rounded-t-full" />}
  </button>
);

// Select Filter Component with Search
const SelectFilter = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const isFiltered = value !== 'all';

  // Get display label for current value
  const getDisplayLabel = () => {
    if (value === 'all') return placeholder;
    const option = options.find(opt => (opt.value || opt) === value);
    return option ? (option.label || option) : value;
  };

  // Filter options based on search
  const filteredOptions = options.filter(opt => {
    const label = (opt.label || opt).toString().toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  });

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (optValue) => {
    onChange({ target: { value: optValue } });
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { value: 'all' } });
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 pl-4 pr-3 py-3 bg-white border rounded-2xl text-[10px] font-black uppercase tracking-widest outline-none transition-all shadow-sm cursor-pointer ${
          isFiltered
            ? 'border-orange-500 text-orange-600'
            : 'border-slate-200 text-slate-600 hover:border-orange-300'
        }`}
      >
        <span className="truncate max-w-[180px]">{getDisplayLabel()}</span>
        {isFiltered ? (
          <button
            onClick={handleClear}
            className="p-0.5 rounded-full hover:bg-orange-100 text-orange-500 transition-colors"
            title="Clear filter"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:border-orange-500 transition-all"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            <button
              onClick={() => handleSelect('all')}
              className={`w-full px-4 py-2.5 text-left text-xs hover:bg-orange-50 transition-colors ${
                value === 'all' ? 'bg-orange-50 text-orange-600 font-bold' : 'text-slate-600'
              }`}
            >
              {placeholder}
            </button>
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-xs text-slate-400 text-center">No results found</div>
            ) : (
              filteredOptions.map((opt) => {
                const optValue = opt.value || opt;
                const optLabel = opt.label || opt;
                return (
                  <button
                    key={optValue}
                    onClick={() => handleSelect(optValue)}
                    className={`w-full px-4 py-2.5 text-left text-xs hover:bg-orange-50 transition-colors ${
                      value === optValue ? 'bg-orange-50 text-orange-600 font-bold' : 'text-slate-600'
                    }`}
                  >
                    {optLabel}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const OEMCertificates = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [projects, setProjects] = useState([]);
  const [allAlerts, setAllAlerts] = useState([]);

  // Filters
  const [selectedProject, setSelectedProject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [certificateFilter, setCertificateFilter] = useState('all');
  const [authorizationFilter, setAuthorizationFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const fetchProjects = async () => {
    try {
      const response = await api.get('/oem/projects/');
      setProjects(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching projects:', err);
      return [];
    }
  };

  const fetchAllAlerts = async (projectList) => {
    try {
      const alertPromises = projectList.map(project =>
        api.get(`/oem/projects/${project.id}/certificate-alerts/`)
          .then(res => ({
            ...res.data,
            project_id: project.id,
            project_code: project.code,
            project_name: project.name
          }))
          .catch(() => null)
      );

      const results = await Promise.all(alertPromises);
      const validResults = results.filter(r => r !== null);

      const flatAlerts = [];
      validResults.forEach(projectData => {
        if (projectData.alerts && projectData.alerts.length > 0) {
          projectData.alerts.forEach(userAlert => {
            userAlert.alerts.forEach(alert => {
              flatAlerts.push({
                ...alert,
                user_id: userAlert.user_id,
                user_name: userAlert.user_name,
                user_email: userAlert.user_email,
                profile_picture: userAlert.profile_picture,
                company_name: userAlert.company_name,
                authorization_level: userAlert.authorization_level,
                authorization_level_color: userAlert.authorization_level_color,
                project_id: projectData.project_id,
                project_code: projectData.project_code,
                project_name: projectData.project_name
              });
            });
          });
        }
      });

      setAllAlerts(flatAlerts);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const projectList = await fetchProjects();
      await fetchAllAlerts(projectList);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const projectList = await fetchProjects();
    await fetchAllAlerts(projectList);
    setIsRefreshing(false);
  };

  // Extract unique values for filters
  const uniqueCompanies = useMemo(() => {
    const companies = [...new Set(allAlerts.map(a => a.company_name).filter(Boolean))];
    return companies.sort();
  }, [allAlerts]);

  const uniqueCertificates = useMemo(() => {
    const certs = [...new Set(allAlerts.map(a => a.qualification_name).filter(Boolean))];
    return certs.sort();
  }, [allAlerts]);

  const uniqueAuthorizations = useMemo(() => {
    const auths = [...new Set(allAlerts.map(a => a.authorization_level).filter(Boolean))];
    return auths.sort();
  }, [allAlerts]);

  // Filter alerts
  const filteredAlerts = useMemo(() => {
    let filtered = allAlerts.filter(alert => {
      if (selectedProject !== 'all' && alert.project_id !== parseInt(selectedProject)) return false;
      if (statusFilter === 'expired' && alert.type !== 'expired') return false;
      if (statusFilter === '30' && alert.type !== 'expiring_30') return false;
      if (statusFilter === '60' && alert.type !== 'expiring_60') return false;
      if (companyFilter !== 'all' && alert.company_name !== companyFilter) return false;
      if (certificateFilter !== 'all' && alert.qualification_name !== certificateFilter) return false;
      if (authorizationFilter !== 'all' && authorizationFilter !== 'none' && authorizationFilter !== 'any') {
        if (alert.authorization_level !== authorizationFilter) return false;
      }
      if (authorizationFilter === 'none' && alert.authorization_level) return false;
      if (authorizationFilter === 'any' && !alert.authorization_level) return false;

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          alert.user_name?.toLowerCase().includes(term) ||
          alert.user_email?.toLowerCase().includes(term) ||
          alert.company_name?.toLowerCase().includes(term) ||
          alert.qualification_name?.toLowerCase().includes(term) ||
          alert.project_code?.toLowerCase().includes(term)
        );
      }
      return true;
    });

    // Sort by days (most urgent first)
    filtered.sort((a, b) => (a.days || 0) - (b.days || 0));

    return filtered;
  }, [allAlerts, selectedProject, statusFilter, companyFilter, certificateFilter, authorizationFilter, searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProject, statusFilter, companyFilter, certificateFilter, authorizationFilter, searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAlerts = filteredAlerts.slice(startIndex, endIndex);

  const stats = {
    total: allAlerts.length,
    expired: allAlerts.filter(a => a.type === 'expired').length,
    expiring_30: allAlerts.filter(a => a.type === 'expiring_30').length,
    expiring_60: allAlerts.filter(a => a.type === 'expiring_60').length,
    uniqueUsers: new Set(allAlerts.map(a => a.user_id)).size
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm">Loading certificates data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard label="Total Alerts" value={stats.total} icon={FileWarning} color="text-slate-900" bg="bg-white" />
        <StatCard label="Expired" value={stats.expired} icon={ShieldAlert} color="text-rose-500" bg="bg-rose-50" border="border-rose-100" />
        <StatCard label="< 30 Days" value={stats.expiring_30} icon={Clock} color="text-orange-500" bg="bg-orange-50" border="border-orange-100" />
        <StatCard label="30-60 Days" value={stats.expiring_60} icon={Clock} color="text-amber-500" bg="bg-amber-50" border="border-amber-100" />
        <StatCard label="Affected Users" value={stats.uniqueUsers} icon={Users} color="text-slate-600" bg="bg-slate-50" />
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
        {/* Status Tabs */}
        <div className="flex items-center border-b border-slate-100">
          <FilterTab active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label="All" count={stats.total} />
          <FilterTab active={statusFilter === 'expired'} onClick={() => setStatusFilter('expired')} label="Expired" count={stats.expired} />
          <FilterTab active={statusFilter === '30'} onClick={() => setStatusFilter('30')} label="< 30 days" count={stats.expiring_30} />
          <FilterTab active={statusFilter === '60'} onClick={() => setStatusFilter('60')} label="30-60 days" count={stats.expiring_60} />
          <div className="flex-1" />
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-5 py-2.5 mr-4 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-orange-500 hover:border-orange-500 transition-all"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Action Bar */}
        <div className="p-6 bg-slate-50/50 flex flex-wrap gap-3 items-center border-b border-slate-100">
          <div className="relative w-64">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 h-4 w-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-orange-500/10 outline-none transition-all shadow-sm"
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            <SelectFilter
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              options={projects.map(p => ({ value: p.id, label: `${p.code} - ${p.name}` }))}
              placeholder="All Projects"
            />
            <SelectFilter
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              options={uniqueCompanies}
              placeholder="All Companies"
            />
            <SelectFilter
              value={certificateFilter}
              onChange={(e) => setCertificateFilter(e.target.value)}
              options={uniqueCertificates}
              placeholder="All Certificates"
            />
            <SelectFilter
              value={authorizationFilter}
              onChange={(e) => setAuthorizationFilter(e.target.value)}
              options={[
                { value: 'any', label: 'With Authorization' },
                { value: 'none', label: 'Without Authorization' },
                ...uniqueAuthorizations.map(a => ({ value: a, label: a }))
              ]}
              placeholder="All Authorizations"
            />
          </div>
        </div>

        {/* Table */}
        {filteredAlerts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-sm font-black text-slate-900 mb-1">All Clear</h3>
            <p className="text-xs text-slate-500">
              {allAlerts.length === 0
                ? 'No certificate alerts found across all projects'
                : 'No alerts match your current filters'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full text-left">
                <thead className="sticky top-0 z-10">
                  <tr className="text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-50">
                    <th className="px-6 py-4 border-b border-slate-100">Technician & Company</th>
                    <th className="px-6 py-4 border-b border-slate-100">Certificate Detail</th>
                    <th className="px-6 py-4 border-b border-slate-100">Expiry Date</th>
                    <th className="px-6 py-4 border-b border-slate-100">Status</th>
                    <th className="px-6 py-4 border-b border-slate-100 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {paginatedAlerts.map((alert, i) => (
                    <tr key={i} className="hover:bg-slate-50/80 transition-all group">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          {alert.profile_picture ? (
                            <img src={getImageUrl(alert.profile_picture)} alt={alert.user_name} className="w-9 h-9 rounded-xl border border-slate-100 shadow-sm object-cover" />
                          ) : (
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black text-xs shadow-sm">
                              {alert.user_name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                          )}
                          <div>
                            <div className="text-xs font-black text-slate-900 leading-none mb-1">{alert.user_name}</div>
                            <div className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{alert.company_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-100 text-slate-400 rounded-lg group-hover:bg-orange-100 group-hover:text-orange-500 transition-colors">
                            <FileText className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="text-[11px] font-black text-slate-800 leading-tight">{alert.qualification_name}</div>
                            <div className="text-[9px] text-slate-400 font-bold uppercase mt-0.5">{alert.project_code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="text-[11px] font-black text-slate-700">{formatDate(alert.expiry_date)}</div>
                        <div className={`text-[9px] font-black uppercase mt-1 ${alert.type === 'expired' ? 'text-rose-500' : 'text-orange-500'}`}>
                          {alert.type === 'expired' ? `${Math.abs(alert.days)} days overdue` : `${alert.days} days remaining`}
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                          alert.type === 'expired'
                            ? 'bg-rose-50 text-rose-600 border-rose-100'
                            : 'bg-orange-50 text-orange-600 border-orange-100'
                        }`}>
                          {alert.type === 'expired' ? 'Action Required' : 'Critical Warning'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => navigate(`/oem/projects/${alert.project_id}`)}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-orange-500 hover:border-orange-500 transition-all shadow-sm"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                          <button className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all shadow-sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/30">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredAlerts.length)} of {filteredAlerts.length} alerts
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-xl border transition-all ${
                      currentPage === 1
                        ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-orange-500 hover:text-orange-500'
                    }`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-9 h-9 rounded-xl text-[11px] font-black transition-all ${
                            currentPage === pageNum
                              ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                              : 'bg-white border border-slate-200 text-slate-600 hover:border-orange-500 hover:text-orange-500'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-xl border transition-all ${
                      currentPage === totalPages
                        ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-orange-500 hover:text-orange-500'
                    }`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OEMCertificates;
